import React from 'react';
import { BrandDealsOverview } from '@/components/brand/BrandDealsOverview';
import { VentureDealsOverview } from '@/components/venture/VentureDealsOverview';
import { BrandTimelineTasksOverview } from '@/components/brand/BrandTimelineTasksOverview';
import { VentureTimelineTasksOverview } from '@/components/venture/VentureTimelineTasksOverview';
import { PDFSOWGenerator } from '@/components/brand/PDFSOWGenerator';
import { VentureSOWGenerator } from '@/components/venture/VentureSOWGenerator';
import { BrandAnalyticsDashboard } from '@/components/brand/BrandAnalyticsDashboard';
import { VentureAnalyticsDashboard } from '@/components/venture/VentureAnalyticsDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileText, Clock, BarChart3, CreditCard, Briefcase, ListChecks } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { BrandPaymentsOverview } from '@/components/brand/BrandPaymentsOverview';
import { VenturePaymentsOverview } from '@/components/venture/VenturePaymentsOverview';
import { PageOnboardingTooltip, OnboardingSlide } from '@/components/onboarding/PageOnboardingTooltip';

const brandDashboardSlides: OnboardingSlide[] = [
  { icon: Briefcase, secondaryIcons: [FileText], title: 'Deal Management Hub', description: 'Create, manage, and track all your deals in one place. View deal status, partner details, and key metrics from the Manage Deals tab.', gradient: 'from-primary/20 to-primary/5' },
  { icon: Clock, secondaryIcons: [ListChecks], title: 'Timeline & Tasks', description: 'Stay on schedule with timeline views and task management. Track deliverables, deadlines, and milestones across all active deals.', gradient: 'from-blue-500/20 to-blue-500/5' },
  { icon: BarChart3, secondaryIcons: [CreditCard], title: 'Analytics & Payments', description: 'Monitor campaign performance with analytics dashboards and manage payment schedules, invoices, and financial summaries.', gradient: 'from-emerald-500/20 to-emerald-500/5' },
];

export const BrandDashboard: React.FC = () => {
  const { accountType } = useUser();
  
  return (
    <div className="space-y-6">
      <PageOnboardingTooltip storageKey="brand_dashboard_tooltip_dismissed" slides={brandDashboardSlides} dialogTitle="Dashboard Walkthrough" />
      
      <Tabs defaultValue="deals" className="w-full">
        {/* h-auto overrides the base TabsList h-11 so the 5 tabs can wrap into
            multiple rows on mobile (grid-cols-2). Without it, rows 2 and 3 are
            clipped behind the page content. */}
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 gap-1 h-auto">
          <TabsTrigger value="deals" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Manage Deals</span>
            <span className="sm:hidden">Deals</span>
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Timeline & Tasks</span>
            <span className="sm:hidden">Tasks</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Payments</span>
            <span className="sm:hidden">Pay</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Analytics</span>
            <span className="sm:hidden">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Upload & Generate SOW</span>
            <span className="sm:hidden">Upload</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {accountType === 'venture' ? 'Investment Term Sheet Generator' : 'Statement of Work Generator'}
              </CardTitle>
              <CardDescription>
                {accountType === 'venture' 
                  ? 'Upload a PDF document to extract content and generate an investment term sheet'
                  : 'Upload a PDF document to extract content and generate a professional Statement of Work'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {accountType === 'venture' ? <VentureSOWGenerator /> : <PDFSOWGenerator />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals" className="space-y-4">
          {accountType === 'venture' ? <VentureDealsOverview /> : <BrandDealsOverview />}
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          {accountType === 'venture' ? <VentureTimelineTasksOverview /> : <BrandTimelineTasksOverview />}
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          {accountType === 'venture' ? <VenturePaymentsOverview /> : <BrandPaymentsOverview />}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {accountType === 'venture' ? <VentureAnalyticsDashboard /> : <BrandAnalyticsDashboard />}
        </TabsContent>
      </Tabs>
    </div>
  );
};
